import { useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, Calendar, Check, House, Loader2, MapPin } from 'lucide-react';
import { Button, Input, Card, Label } from '../components/ui.jsx';
import { api } from '../lib/api.js';

export default function HomePage() {
  const [requiresUnlock, setRequiresUnlock] = useState(false);
  const [adminCode, setAdminCode] = useState('');
  const [unlocking, setUnlocking] = useState(false);
  const [unlockError, setUnlockError] = useState('');
  const [isUnlocked, setIsUnlocked] = useState(false);

  const [name, setName] = useState('');
  const [departure, setDeparture] = useState('');
  const [anreise, setAnreise] = useState('');
  const [abreise, setAbreise] = useState('');
  const [trip, setTrip] = useState(null);
  const [error, setError] = useState('');

  const tryUnlock = async (e) => {
    e?.preventDefault();
    setUnlocking(true);
    setUnlockError('');
    try {
      await api.post('/api/admin/verify', { code: adminCode });
      setIsUnlocked(true);
      setAdminCode('');
    } catch (err) {
      setUnlockError(err.message || 'Code ungültig');
    } finally {
      setUnlocking(false);
    }
  };

  const handleAnreiseChange = (e) => {
    const newAnreise = e.target.value;
    setAnreise(newAnreise);
    if (abreise && abreise < newAnreise) setAbreise('');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    try {
      const newTrip = await api.post('/api/trips', {
        name,
        departure,
        anreise,
        abreise,
        code: adminCode,
      });
      setTrip(newTrip);
    } catch (err) {
      if (err.message?.includes('Admin-Code')) {
        setRequiresUnlock(true);
        setUnlockError('Bitte Admin-Code eingeben');
      } else {
        setError(err.message);
      }
    }
  };

  if (trip) {
    return (
      <div className="mx-auto max-w-md px-4 py-16">
        <div className="mb-6 flex items-center gap-2">
          <span className="flex h-8 w-8 items-center justify-center rounded-md bg-neutral-900 text-white">
            <Check size={16} />
          </span>
          <h1 className="text-xl font-semibold tracking-tight">Reise erstellt</h1>
        </div>
        <Card className="p-6">
          <dl className="divide-y divide-neutral-100">
            <div className="flex items-baseline justify-between gap-4 py-3">
              <dt className="shrink-0 text-sm text-neutral-500">Name</dt>
              <dd className="text-right text-sm font-medium">{trip.name}</dd>
            </div>
            {trip.departure && (
              <div className="flex items-baseline justify-between gap-4 py-3">
                <dt className="shrink-0 text-sm text-neutral-500">Abfahrtsort</dt>
                <dd className="text-right text-sm font-medium">{trip.departure}</dd>
              </div>
            )}
            <div className="flex items-baseline justify-between gap-4 py-3">
              <dt className="shrink-0 text-sm text-neutral-500">Einladungscode</dt>
              <dd className="rounded-md bg-neutral-100 px-2 py-0.5 font-mono text-xs">
                {trip.invite_code}
              </dd>
            </div>
            <div className="flex items-baseline justify-between gap-4 py-3">
              <dt className="shrink-0 text-sm text-neutral-500">Einladungslink</dt>
              <dd className="text-right text-sm">
                <a
                  href={`/trip/${trip.invite_code}`}
                  className="break-all text-blue-600 hover:underline"
                >
                  {window.location.origin}/trip/{trip.invite_code}
                </a>
              </dd>
            </div>
          </dl>
          <Link
            to={`/trip/${trip.invite_code}`}
            className="mt-4 inline-flex h-9 w-full items-center justify-center gap-2 rounded-md bg-neutral-900 text-sm font-medium text-white shadow-sm transition hover:bg-neutral-700"
          >
            Zur Wunschliste <ArrowRight size={16} />
          </Link>
        </Card>
      </div>
    );
  }

  if (requiresUnlock && !isUnlocked) {
    return (
      <div className="mx-auto max-w-md px-4 py-16">
        <div className="mb-6 flex items-center gap-2">
          <span className="flex h-8 w-8 items-center justify-center rounded-md bg-neutral-900 text-white">
            <House size={16} />
          </span>
          <div>
            <h1 className="text-xl font-semibold tracking-tight">Admin-Bereich</h1>
            <p className="text-sm text-neutral-500">
              Bitte Admin-Code eingeben, um eine Reise zu erstellen.
            </p>
          </div>
        </div>
        <Card className="p-6">
          <form onSubmit={tryUnlock} className="space-y-4">
            <div className="space-y-1.5">
              <Label>Admin-Code</Label>
              <Input
                type="password"
                value={adminCode}
                onChange={(e) => setAdminCode(e.target.value)}
                placeholder="Code eingeben"
                autoFocus
                required
              />
            </div>
            {unlockError && <p className="text-sm text-red-600">{unlockError}</p>}
            <Button type="submit" className="w-full" disabled={unlocking}>
              {unlocking && <Loader2 size={16} className="animate-spin" />}
              {unlocking ? 'Prüfe...' : 'Freischalten'}
            </Button>
          </form>
        </Card>
      </div>
    );
  }

  return (
    <div className="mx-auto max-w-md px-4 py-16">
      <div className="mb-6 flex items-center gap-2">
        <span className="flex h-8 w-8 items-center justify-center rounded-md bg-neutral-900 text-white">
          <House size={16} />
        </span>
        <div>
          <h1 className="text-xl font-semibold tracking-tight">Reisegruppe</h1>
          <p className="text-sm text-neutral-500">
            Gemeinsam die perfekte Unterkunft finden
          </p>
        </div>
      </div>

      <Card className="p-6">
        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="space-y-1.5">
            <Label>Reisetitel</Label>
            <Input
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="z.B. Städtetrip Prag"
              required
            />
          </div>
          <div className="space-y-1.5">
            <Label>Abfahrtsort</Label>
            <Input
              type="text"
              value={departure}
              onChange={(e) => setDeparture(e.target.value)}
              placeholder="z.B. Dresden"
              required
            />
            <p className="flex items-center gap-1 text-xs text-neutral-400">
              <MapPin size={12} /> Von hier aus berechnen wir die Fahrzeit.
            </p>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5">
              <Label>Anreise</Label>
              <Input
                type="date"
                value={anreise}
                onChange={handleAnreiseChange}
                min={new Date().toISOString().split('T')[0]}
              />
            </div>
            <div className="space-y-1.5">
              <Label>Abreise</Label>
              <Input
                type="date"
                value={abreise}
                onChange={(e) => setAbreise(e.target.value)}
                min={anreise || new Date().toISOString().split('T')[0]}
                disabled={!anreise}
              />
            </div>
          </div>
          {error && <p className="text-sm text-red-600">{error}</p>}
          <Button type="submit" className="w-full">
            <Calendar size={16} /> Reise erstellen
          </Button>
        </form>
      </Card>

      <div className="mt-8 grid grid-cols-1 gap-x-6 gap-y-2 sm:grid-cols-2">
        {[
          'Gemeinsame Favoritenliste',
          'Kommentare zu Unterkünften',
          'Karte mit echten Koordinaten',
          'Fahrzeit ab Abfahrtsort',
          'Verfügbarkeit für Reisedaten',
          'Einfache Einladung per Link',
        ].map((feature) => (
          <span key={feature} className="inline-flex items-center gap-2 text-sm text-neutral-600">
            <Check size={14} className="shrink-0 text-neutral-400" /> {feature}
          </span>
        ))}
      </div>
    </div>
  );
}
